import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import { StepFlowFooter } from "../../components/step-flow/StepFlowShell";
import Button from "../../components/button/Button";
import SectionTitle from "../profile/components/SectionTitle";
// @ts-expect-error - JSX ファイルの型定義なし
import OsFooter from "../os/components/OsFooter";

import mgkReportSample from "../../assets/images/mgk/mgk_report_sample.webp";
import MgkHero from "./components/MgkHero";
import MgkTeaser from "./components/MgkTeaser";
import MgkApplyFlow from "./components/MgkApplyFlow";
import {
  MGK_BODY_PARAGRAPHS,
  MGK_CATCHPHRASE,
  MGK_CATCH_DESCRIPTION,
  MGK_PAMPHLET_URL,
  MGK_TITLE,
  MGK_WORKFLOW,
} from "./mgkContent";

// ===== Styled =====
// 幅の構成は公開プロフィールページと同じ:
//   セクション見出し・ボタン・ワークフロー = .u-wrapper と同じ 1140px / 読みものは 640px 中央寄せ
// 文字は /careers に揃える（見出し = AXIS L clamp、本文 = bodytext・行間 200%・両端揃え）。

const Main = styled.main`
  position: fixed;
  inset: 0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const Section = styled.section.attrs({ className: "u-w100 u-col u-items-center" })`
  padding: 96px 24px 0;

  @media (max-width: 767px) {
    padding: 64px 24px 0;
  }
`;

/* .u-wrapper 相当（1140px） */
const Wide = styled.div.attrs({ className: "u-w100 u-col u-items-center u-gap48" })`
  max-width: 1140px;
`;

/* 読みもの幅（640px） */
const Reading = styled.div.attrs({ className: "u-w100 u-col u-gap24" })`
  max-width: 640px;
`;

/* キャッチは 1140px 幅に置き、PC では 1 行で収まるようにする */
const CatchBlock = styled.div.attrs({ className: "u-w100 u-col u-items-center u-gap24" })`
  max-width: 1140px;
`;

/* /careers の「動けば、世界は変わる。」と同じ見出し */
const Catchphrase = styled.h2`
  font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
  font-weight: normal;
  font-size: clamp(1.313rem, 0.849rem + 2.32vw, 2.5rem);
  line-height: 187.5%;
  color: #000;
  text-align: center;
`;

const CatchDescription = styled.p.attrs({ className: "bodytext" })`
  max-width: 640px;
  line-height: 200%;
  color: var(--soremo-gray);
  text-align: center;
`;

const Body = styled.p.attrs({ className: "bodytext" })`
  line-height: 200%;
  color: #000;
  text-align: justify;
  word-break: normal;
`;

const ReportImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
  border: 1px solid var(--soremo-border);
`;

/* WORKFLOW: 縦並び。各ステップは PC では「文章 | 画像」の 2 カラムで幅いっぱいに */
const WorkflowList = styled.ol.attrs({ className: "u-w100 u-col u-gap48" })`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const WorkflowItem = styled.li`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: center;

  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    gap: 48px;
  }
`;

const WorkflowText = styled.div.attrs({ className: "u-col u-gap16" })``;

/* /careers の PrincipleItem と同じ（番号 + 見出し） */
const WorkflowHead = styled.div.attrs({ className: "u-row u-items-start u-gap16" })``;

const Counter = styled.span.attrs({ className: "heading-spacing-18" })`
  line-height: 100%;
  letter-spacing: 0;
  user-select: none;
  flex-shrink: 0;

  width: clamp(2.5rem, 2.11rem + 1.95vw, 3.5rem);
  height: clamp(2.5rem, 2.11rem + 1.95vw, 3.5rem);

  border-radius: 50%;
  border: 1px solid #000;

  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
`;

const WorkflowTitle = styled.h3.attrs({ className: "heading-spacing-18" })`
  color: #000;
  padding-top: 12px;
  line-height: 150%;
`;

const WorkflowImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
  background-color: var(--soremo-background);
`;

const WorkflowBody = styled.p.attrs({ className: "bodytext" })`
  line-height: 200%;
  color: #000;
  text-align: justify;
  word-break: normal;
`;

const WorkflowNote = styled.p.attrs({ className: "bodytext-13" })`
  line-height: 175%;
  color: var(--soremo-light-gray);
`;

/* ボタンは他と同じ左端始まり（SectionTitle の下） */
const PamphletActions = styled.div.attrs({ className: "u-w100 u-row" })``;

const FooterSpacer = styled.div`
  height: 128px;
`;

/**
 * MUSIC GATE KEEPER（/mgk）。旧 Django SSR（creator/mgk.html）の SPA 移植。
 * LP（ヒーロー → 概要 → ティーザー → 本文 → WORKFLOW → PAMPHLET）の下部に
 * 「戻る / プランを見る」の幅上限付きフッターを置き、プラン選択以降は
 * 共通 StepFlowLayout（MgkApplyFlow）へ切り替える。送信は contact API を再利用。
 */
const Mgk: React.FC = () => {
  useDocumentTitle(MGK_TITLE);
  const navigate = useNavigate();
  const [isApplying, setIsApplying] = useState(false);

  /* システム共通の Button（<button>）で PDF をダウンロードさせる */
  const handleDownloadPamphlet = useCallback(() => {
    const a = document.createElement("a");
    a.href = MGK_PAMPHLET_URL;
    a.download = "MusicGateKeeper.pdf";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  const handleExit = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  }, [navigate]);

  if (isApplying) {
    return <MgkApplyFlow onClose={() => setIsApplying(false)} />;
  }

  return (
    <Main>
      <ScrollArea>
        <MgkHero />

        <Section>
          <CatchBlock>
            <Catchphrase>{MGK_CATCHPHRASE}</Catchphrase>
            <CatchDescription>
              {MGK_CATCH_DESCRIPTION.map((line) => (
                <React.Fragment key={line}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </CatchDescription>
          </CatchBlock>
        </Section>

        <Section>
          <Wide>
            <MgkTeaser />
          </Wide>
        </Section>

        <Section>
          <Reading>
            {MGK_BODY_PARAGRAPHS.map((p) => (
              <Body key={p.slice(0, 16)}>{p}</Body>
            ))}
            <ReportImage src={mgkReportSample} alt="レポートサンプル" loading="lazy" />
          </Reading>
        </Section>

        <Section>
          <Wide>
            <SectionTitle>Workflow</SectionTitle>
            <WorkflowList>
              {MGK_WORKFLOW.map((step, index) => (
                <WorkflowItem key={step.title}>
                  <WorkflowText>
                    <WorkflowHead>
                      <Counter>{index + 1}</Counter>
                      <WorkflowTitle>{step.title}</WorkflowTitle>
                    </WorkflowHead>
                    <WorkflowBody>{step.body}</WorkflowBody>
                    {step.note ? <WorkflowNote>{step.note}</WorkflowNote> : null}
                  </WorkflowText>
                  <WorkflowImage src={step.image} alt={step.title} loading="lazy" />
                </WorkflowItem>
              ))}
            </WorkflowList>
          </Wide>
        </Section>

        <Section>
          <Wide>
            <SectionTitle>Pamphlet</SectionTitle>
            <PamphletActions>
              <Button theme="primary" leftIcon="download" onClick={handleDownloadPamphlet}>
                説明資料をダウンロード
              </Button>
            </PamphletActions>
          </Wide>
        </Section>

        <FooterSpacer />
        <OsFooter />
      </ScrollArea>

      <StepFlowFooter
        width="wide"
        secondaryAction={{ label: "戻る", onClick: handleExit, leftIcon: "chevron_left" }}
        primaryAction={{
          label: "プランを見る",
          onClick: () => setIsApplying(true),
          rightIcon: "chevron_right",
        }}
      />
    </Main>
  );
};

export default Mgk;
